import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, ArrowLeft, Sparkles, Loader2, Lightbulb, Scale, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config';

const EXAMPLE_REGULATIONS = [
  {
    id: 'vat-split-payment',
    title: 'Mechanizm Podzielonej Płatności (Split Payment)',
    article: 'Art. 108a-108e ustawy o VAT'
  },
  {
    id: 'ksef',
    title: 'Krajowy System e-Faktur (KSeF)',
    article: 'Art. 106na-106nd ustawy o VAT'
  },
  {
    id: 'maly-podatnik',
    title: 'Status małego podatnika VAT',
    article: 'Art. 2 pkt 25 ustawy o VAT'
  },
  {
    id: 'korekta-vat',
    title: 'Korekta podatku naliczonego',
    article: 'Art. 89a ustawy o VAT'
  }
];

export default function RegulationTranslatorPage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleTranslate = async (inputQuery = null) => {
    const q = inputQuery || query;
    if (!q.trim()) {
      toast.error('Wprowadź przepis do wyjaśnienia');
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/regulation/translate`, {
        regulation_text: q
      }, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        timeout: 60000
      });
      
      setResult(response.data);
      toast.success('Wyjaśnienie gotowe!');
    } catch (error) {
      console.error('Translation error:', error);
      toast.error('Błąd podczas wyjaśniania przepisu');
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setQuery(`Wyjaśnij przepis: ${example.title} (${example.article})`);
    handleTranslate(`Wyjaśnij przepis: ${example.title} (${example.article})`);
  };

  return (
    <div className="min-h-screen bg-[#f8fafb] dark:bg-slate-900">
      {/* Header */}
      <div className="border-b bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl sticky top-0 z-10 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/dashboard')} 
              data-testid="back-to-dashboard"
              className="gap-2 text-slate-500 hover:text-slate-700"
            >
              <ArrowLeft className="w-4 h-4" /> Powrót
            </Button>
            <div className="h-6 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-teal-200 dark:shadow-none">
                <BookOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-slate-800 dark:text-white">Tłumacz Przepisów</h1>
                <p className="text-xs text-slate-400">Proste wyjaśnienia skomplikowanych regulacji</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 lg:p-8">
        {/* Quick examples */}
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-500 dark:text-slate-400 mb-3">Popularne przepisy:</h3>
          <div className="flex flex-wrap gap-2">
            {EXAMPLE_REGULATIONS.map((ex) => (
              <Button
                key={ex.id}
                variant="outline"
                size="sm"
                onClick={() => handleExampleClick(ex)}
                className="text-xs hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700"
              >
                {ex.title}
              </Button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Input */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="p-5 bg-gradient-to-r from-teal-500 to-emerald-600">
              <h2 className="font-bold text-white text-lg flex items-center gap-2">
                <Scale className="w-5 h-5" />
                Przepis do wyjaśnienia
              </h2>
              <p className="text-white/80 text-sm mt-1">Wpisz artykuł, paragraf lub opis przepisu</p>
            </div>
            
            <div className="p-5">
              <textarea
                data-testid="regulation-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full h-48 p-4 rounded-xl border-2 border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-sm resize-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 transition-all"
                placeholder={`Przykłady:
• Art. 86 ust. 1 ustawy o VAT
• Czym jest mechanizm podzielonej płatności?
• Kiedy muszę wystawić fakturę korygującą?
• Jak działa ulga na złe długi?`}
              />

              <Button
                data-testid="translate-btn"
                onClick={() => handleTranslate()}
                disabled={loading || !query.trim()}
                className="w-full mt-5 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white h-12 rounded-xl shadow-lg"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Tłumaczę na prosty język...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" /> Wyjaśnij przepis
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Right: Results */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="p-5 border-b border-slate-100 dark:border-slate-700">
              <h3 className="font-semibold text-slate-800 dark:text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-yellow-500" />
                Wyjaśnienie
              </h3>
            </div>
            
            {result ? (
              <ScrollArea className="h-[500px]">
                <div className="p-5 space-y-4">
                  {/* Simple explanation */}
                  <div className="p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-100 dark:border-teal-800">
                    <h4 className="font-bold text-teal-800 dark:text-teal-200 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" /> W prostych słowach:
                    </h4>
                    <p className="text-teal-700 dark:text-teal-300 leading-relaxed">{result.simple_explanation}</p>
                  </div>

                  {/* Key points */}
                  {result.key_points && result.key_points.length > 0 && (
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                      <h4 className="font-bold text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4" /> Kluczowe punkty:
                      </h4>
                      <ul className="space-y-2">
                        {result.key_points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-blue-700 dark:text-blue-300">
                            <span className="text-blue-500 font-bold">•</span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Practical examples */}
                  {result.practical_example && (
                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-100 dark:border-amber-800">
                      <h4 className="font-bold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Przykład praktyczny:
                      </h4>
                      <p className="text-amber-700 dark:text-amber-300 leading-relaxed">{result.practical_example}</p>
                    </div>
                  )}

                  {/* Who it affects */}
                  {result.who_affected && (
                    <div className="p-4 bg-violet-50 dark:bg-violet-900/20 rounded-xl border border-violet-100 dark:border-violet-800">
                      <h4 className="font-bold text-violet-800 dark:text-violet-200 mb-2">👥 Kogo dotyczy:</h4>
                      <p className="text-violet-700 dark:text-violet-300">{result.who_affected}</p>
                    </div>
                  )}

                  {/* Legal basis */}
                  {result.legal_basis && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
                      <h5 className="font-semibold text-slate-700 dark:text-slate-200 mb-2">📜 Podstawa prawna:</h5>
                      <p className="text-sm text-slate-600 dark:text-slate-300">{result.legal_basis}</p>
                    </div>
                  )}

                  {/* Warnings */}
                  {result.warnings && result.warnings.length > 0 && (
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-100 dark:border-red-800">
                      <h4 className="font-bold text-red-800 dark:text-red-200 mb-2">⚠️ Uwaga:</h4>
                      <ul className="space-y-1">
                        {result.warnings.map((warning, idx) => (
                          <li key={idx} className="text-red-700 dark:text-red-300 text-sm">{warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-[400px] flex items-center justify-center">
                <div className="text-center p-8">
                  <div className="w-20 h-20 rounded-2xl bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-10 h-10 text-slate-300 dark:text-slate-500" />
                  </div>
                  <h4 className="font-semibold text-slate-600 dark:text-slate-300 mb-2">Brak wyjaśnienia</h4>
                  <p className="text-sm text-slate-400 max-w-xs">
                    Wpisz przepis, który chcesz zrozumieć, a KAI wyjaśni go prostym językiem
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info section */}
        <div className="mt-6 p-5 bg-gradient-to-r from-teal-50 to-emerald-50 dark:from-teal-900/20 dark:to-emerald-900/20 rounded-2xl border border-teal-100 dark:border-teal-800">
          <h4 className="font-semibold text-slate-800 dark:text-white mb-3">💡 Jak działa Tłumacz Przepisów?</h4>
          <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
            Tłumacz Przepisów to narzędzie AI, które analizuje skomplikowane regulacje prawne i wyjaśnia je <strong>prostym, zrozumiałym językiem</strong>. 
            Każde wyjaśnienie zawiera: główną ideę przepisu, praktyczne przykłady zastosowania, informacje kogo dotyczy oraz podstawę prawną. 
            Idealne dla księgowych, przedsiębiorców i wszystkich, którzy chcą szybko zrozumieć zawiłości prawa podatkowego.
          </p>
        </div>
      </div>
    </div>
  );
}
